// MARK://dhk8/admin-shell/pages/modules/RolesPage
// Módulo 3 (#3 F4) — Roles del negocio → dhk8-platform-auth por el grafo.
// Tabla de roles (built-ins sintéticos + custom del tenant). El dueño/gerente crea
// un ROL CUSTOM eligiendo qué MÓDULOS deja VER (checkboxes desde el catálogo
// canónico @dockethub/contracts MODULES) y lo edita/borra. Los built-ins
// (Integrado) son de SOLO LECTURA — sirven de referencia/plantilla. La asignación
// de un rol a un usuario vive en Personal (UsersPage), no aquí.
//
// Cada fila navega a la FICHA del rol (/roles/:id) — General + Módulos/Permisos +
// Miembros + Historial — donde se EDITA (built-ins en solo lectura). "Crear rol"
// se queda como sheet aquí; el editar de un rol existente vive en su ficha.
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, ChevronRight, ShieldCheck } from 'lucide-react'
import { MODULES, type AppKey } from '@dockethub/contracts/shared'
import * as roles from '../../lib/clients/roles.js'
import type { Role } from '../../lib/clients/roles.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import Sheet from '../../components/Sheet.js'

// Etiqueta amistosa de un rol: built-ins traen su nombre es-MX en `description`;
// si no, Title-case del `name` técnico.
function roleTitle(r: Role): string {
  if (r.builtin && r.description) return r.description
  return r.name.charAt(0).toUpperCase() + r.name.slice(1)
}

// Agrupa el catálogo para que el picker se escanee fácil: primero los módulos del
// Admin, luego los del POS, ordenados por etiqueta. (Un módulo puede estar en ambas.)
const MODULE_GROUPS: { app: AppKey; label: string; mods: typeof MODULES[number][] }[] = [
  { app: 'admin', label: 'En el Admin', mods: MODULES.filter((m) => m.apps.includes('admin')).slice().sort((a, b) => a.label.localeCompare(b.label, 'es')) },
  { app: 'pos', label: 'En el POS', mods: MODULES.filter((m) => m.apps.includes('pos')).slice().sort((a, b) => a.label.localeCompare(b.label, 'es')) },
]

interface FormState { name: string; description: string; modules: Set<string> }
const emptyForm: () => FormState = () => ({ name: '', description: '', modules: new Set() })

export default function RolesPage() {
  const navigate = useNavigate()
  const list = useAsync((s) => roles.listRoles(s), [])

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<FormState>(emptyForm())
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Solo CREAR vive aquí; el editar de un rol existente migró a su ficha (/roles/:id).
  function openCreate() { setForm(emptyForm()); setFormError(null); setOpen(true) }
  function close() { setOpen(false) }

  function toggleModule(key: string) {
    setForm((f) => {
      const next = new Set(f.modules)
      if (next.has(key)) next.delete(key); else next.add(key)
      return { ...f, modules: next }
    })
  }

  async function submit() {
    setBusy(true); setFormError(null)
    try {
      const modules = [...form.modules]
      const description = form.description.trim() || null
      await roles.createRole({ name: form.name.trim(), description, modules })
      setOpen(false)
      list.reload()
    } catch (e) { setFormError(e instanceof Error ? e.message : String(e)) } finally { setBusy(false) }
  }

  async function remove(r: Role) {
    if (!confirm(`¿Eliminar el rol “${roleTitle(r)}”? Los usuarios con este rol quedarán sin sus módulos hasta reasignarles otro.`)) return
    try { await roles.deleteRole(r.id); list.reload() }
    catch (e) { alert(e instanceof Error ? e.message : 'No se pudo eliminar el rol') }
  }

  const moduleLabel = useMemo(() => new Map<string, string>(MODULES.map((m) => [m.key, m.label])), [])
  function moduleSummary(r: Role): string {
    if (r.modules.length === 0) return 'Sin módulos'
    const names = r.modules.map((k) => moduleLabel.get(k) ?? k)
    if (names.length <= 3) return names.join(' · ')
    return `${names.slice(0, 3).join(' · ')} +${names.length - 3}`
  }

  const selectedCount = form.modules.size

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Roles</h1>
          <p className="adm-page__sub">Define roles a la medida eligiendo qué módulos puede ver cada uno. Asigna el rol a tu personal desde la pantalla de Personal.</p>
        </div>
        <div className="adm-page__actions">
          <button className="adm-btn adm-btn--primary" onClick={openCreate}><Plus size={16} /> Crear rol</button>
        </div>
      </div>

      {asyncState(list.loading, list.error, list.reload)}
      {list.data && <div className="adm-card adm-card--pad0"><div className="adm-list">
        {list.data.length === 0 && <div className="adm-empty">Sin roles</div>}
        {list.data.map((r) => (
          <div key={r.id} className="adm-row rol-row" role="button" tabIndex={0}
            onClick={() => navigate(`/roles/${r.id}`)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/roles/${r.id}`) } }}>
            <div className="adm-row__main">
              <div className="adm-row__title">{roleTitle(r)}</div>
              <div className="adm-row__meta">{r.description && !r.builtin ? `${r.description} · ` : ''}{moduleSummary(r)}</div>
            </div>
            <div className="adm-row__tail">
              <span className={`adm-badge ${r.builtin ? 'adm-badge--info' : 'adm-badge--ok'}`}>{r.builtin ? 'Integrado' : 'Personalizado'}</span>
              {r.builtin
                ? <span className="adm-row__meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ShieldCheck size={14} aria-hidden /> Solo lectura</span>
                : <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={(e) => { e.stopPropagation(); remove(r) }} aria-label="Eliminar"><Trash2 size={15} /></button>}
              <ChevronRight size={16} className="adm-muted" aria-hidden />
            </div>
          </div>
        ))}
      </div></div>}

      {/* Crear rol (el editar de un rol existente vive en su ficha /roles/:id) */}
      <Sheet open={open} title="Crear rol" onClose={close}
        footer={<>
          <button className="adm-btn" onClick={close}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={submit} disabled={busy || !form.name.trim()}>Crear rol</button>
        </>}>
        <label className="adm-field"><span className="adm-field__label">Nombre del rol</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="p. ej. Anfitrión, Supervisor de barra…" /></label>
        <label className="adm-field"><span className="adm-field__label">Descripción (opcional)</span><input className="adm-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="¿Para qué sirve este rol?" /></label>

        <div className="adm-field">
          <span className="adm-field__label">Módulos que puede ver {selectedCount > 0 && <span className="adm-badge adm-badge--info" style={{ marginLeft: 6 }}>{selectedCount}</span>}</span>
          <p className="adm-row__meta" style={{ margin: '0 0 8px' }}>Marca lo que este rol verá en el menú. Lo que no marques quedará oculto para ese usuario.</p>
          <div className="rol-modgrid">
            {MODULE_GROUPS.map((g) => (
              <div key={g.app} className="rol-modgrid__group">
                <div className="rol-modgrid__grouphead">{g.label}</div>
                {g.mods.map((m) => {
                  const checked = form.modules.has(m.key)
                  return (
                    <label key={m.key} className={`rol-mod${checked ? ' rol-mod--on' : ''}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleModule(m.key)} />
                      <span className="rol-mod__text">
                        <span className="rol-mod__label">{m.label}</span>
                        <span className="rol-mod__desc">{m.description}</span>
                      </span>
                    </label>
                  )
                })}
              </div>
            ))}
          </div>
        </div>

        {formError && <div className="adm-banner adm-banner--danger" role="alert">{formError}</div>}
      </Sheet>

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .rol-row { cursor: pointer; }
  .rol-row:hover { background: var(--pos-elevated); }
  .rol-modgrid { display: flex; flex-direction: column; gap: var(--pos-space-4); }
  .rol-modgrid__group { display: flex; flex-direction: column; gap: 4px; }
  .rol-modgrid__grouphead { font-size: var(--pos-text-xxs); font-weight: var(--pos-weight-semibold); letter-spacing: var(--pos-tracking-widest); text-transform: uppercase; color: var(--pos-fg-tertiary); margin-bottom: 2px; }
  .rol-mod { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; border: 1px solid var(--pos-separator); border-radius: var(--pos-radius-md); background: var(--pos-surface); cursor: pointer; transition: border-color var(--pos-duration-fast) var(--pos-ease-out), background var(--pos-duration-fast) var(--pos-ease-out); }
  .rol-mod:hover { background: var(--pos-elevated); }
  .rol-mod--on { border-color: var(--pos-tint); background: var(--pos-tint-fill); }
  .rol-mod input[type="checkbox"] { margin-top: 2px; flex-shrink: 0; width: 18px; height: 18px; accent-color: var(--pos-tint); cursor: pointer; }
  .rol-mod__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .rol-mod__label { font-size: var(--pos-text-sm); font-weight: var(--pos-weight-medium); color: var(--pos-fg); }
  .rol-mod__desc { font-size: var(--pos-text-xs); color: var(--pos-fg-tertiary); line-height: var(--pos-leading-snug, 1.35); }
`
